/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  Amounts,
  HostPortPath,
  HttpStatusCode,
  Paytos,
  TalerCoreBankErrorsByMethod,
  TalerCorebankApi,
  TalerError,
  TalerUriAction,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  useBankCoreApiContext,
  useTranslationContext,
  utils,
} from "@gnu-taler/web-util/browser";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { useSettingsContext } from "../../context/settings.js";
import {
  revalidateWithdrawalDetails,
  useWithdrawalDetails,
} from "../../hooks/account.js";
import { useBankState } from "../../hooks/bank-state.js";
import { useSessionState } from "../../hooks/session.js";
import { Props, State, WithdrawalOperationProps } from "./index.js";

export type WithdrawalDetailsResult = Exclude<
  ReturnType<typeof useWithdrawalDetails>,
  undefined
>;

interface WithdrawalStateInput extends Omit<
  WithdrawalOperationProps,
  "clearWhenTerminal"
> {
  result: WithdrawalDetailsResult;
  bankIntegrationApiBaseUrl: HostPortPath;
  loadingErrorTitle: State.LoadingError["title"];
  onRetry(): void;
}

export function isTerminalWithdrawalResult(
  result: WithdrawalDetailsResult,
): boolean {
  return (
    !(result instanceof TalerError) &&
    (result.type === "fail" ||
      result.body.status === "aborted" ||
      result.body.status === "confirmed")
  );
}

export function buildWithdrawalOperationState({
  result,
  operationId,
  bankIntegrationApiBaseUrl,
  routeClose,
  onAbort,
  onContinueLater,
  focus,
  loadingErrorTitle,
  onRetry,
}: WithdrawalStateInput): State {
  if (result instanceof TalerError) {
    return {
      status: "loading-error",
      error: result,
      title: loadingErrorTitle,
      onRetry,
    };
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.NotFound:
        return { status: "aborted", error: undefined, routeClose };
      default:
        assertUnreachable(result);
    }
  }

  const { body: data } = result;
  if (data.status === "aborted") {
    return { status: "aborted", error: undefined, routeClose };
  }
  if (data.status === "confirmed") {
    return {
      status: "confirmed",
      error: undefined,
      routeClose,
      cashAcceptor: data.no_amount_to_wallet === true,
    };
  }

  const amount = !data.amount ? undefined : Amounts.parse(data.amount);
  const suggestedAmount = !data.suggested_amount
    ? undefined
    : Amounts.parse(data.suggested_amount);

  if (data.status === "pending") {
    return {
      status: "ready",
      error: undefined,
      uri: {
        type: TalerUriAction.Withdraw,
        bankIntegrationApiBaseUrl,
        withdrawalOperationId: operationId,
        ...(data.no_amount_to_wallet ? { externalConfirmation: true } : {}),
      },
      amount: amount
        ? { mode: "fixed", value: amount }
        : suggestedAmount
          ? { mode: "suggested", value: suggestedAmount }
          : undefined,
      progressMode: amount ? "amount-first" : "wallet-first",
      routeClose,
      focus,
      operationId,
      onAbort,
      cashAcceptor: data.no_amount_to_wallet === true,
    };
  }
  if (!data.selected_reserve_pub) {
    return {
      status: "invalid-reserve",
      error: undefined,
      reserve: data.selected_reserve_pub,
    };
  }

  const account = !data.selected_exchange_account
    ? undefined
    : Paytos.fromString(data.selected_exchange_account);
  if (!account || account.tag === "error" || !account.value.targetType) {
    return {
      status: "invalid-payto",
      error: undefined,
      payto: data.selected_exchange_account,
    };
  }
  return {
    status: "need-confirmation",
    error: undefined,
    details: {
      account: account.value,
      reserve: data.selected_reserve_pub,
      username: data.username,
      amount: data.no_amount_to_wallet
        ? {
            mode: "bank-selected",
            suggested: suggestedAmount,
          }
        : amount
          ? {
              mode: "fixed",
              value: amount,
            }
          : { mode: "missing" },
    },
    account: data.username,
    operationId,
    progressMode:
      data.no_amount_to_wallet || suggestedAmount
        ? "wallet-first"
        : "amount-first",
    cashAcceptor: data.no_amount_to_wallet === true,
    onAbort,
    onContinueLater,
  };
}

export function useComponentState({
  routeClose,
  onAbort,
  onContinueLater,
  onOperationCreated,
  focus,
  creationMode = "suggested",
}: Props): utils.RecursiveState<State> {
  const settings = useSettingsContext();
  const [bankState, updateBankState] = useBankState();
  const { state: credentials } = useSessionState();
  const creds = credentials.status !== "loggedIn" ? undefined : credentials;
  const {
    config,
    lib: { bank },
    url: backendUrl,
  } = useBankCoreApiContext();

  const [failure, setFailure] = useState<
    TalerCoreBankErrorsByMethod<"createWithdrawal"> | undefined
  >();
  const amount = settings.defaultSuggestedAmount;
  const creationGeneration = useRef(0);

  const doSilentStart = useCallback(
    async (generation: number) => {
      if (!creds) return;
      const params: TalerCorebankApi.BankAccountCreateWithdrawalRequest =
        creationMode === "cash-acceptor"
          ? {
              no_amount_to_wallet: true,
            }
          : {
              suggested_amount: Amounts.stringify(
                Amounts.parseOrThrow(`${config.currency}:${amount}`),
              ),
            };

      const resp = await bank.createWithdrawal(creds, params);
      if (generation !== creationGeneration.current) return;
      if (resp.type === "fail") {
        setFailure(resp);
        return;
      }
      updateBankState("activeWithdrawal", {
        operationId: resp.body.withdrawal_id,
        username: creds.username,
        backendBaseUrl: backendUrl.href,
        confirmationDeferred: false,
        kind: creationMode === "cash-acceptor" ? "cash-acceptor" : "standard",
      });
      onOperationCreated?.(resp.body.withdrawal_id);
    },
    [
      amount,
      bank,
      backendUrl.href,
      config.currency,
      creationMode,
      creds,
      onOperationCreated,
      updateBankState,
    ],
  );

  const activeWithdrawal = bankState.activeWithdrawal;
  const withdrawalOperationId =
    activeWithdrawal !== undefined &&
    creds !== undefined &&
    activeWithdrawal.username === creds.username &&
    activeWithdrawal.backendBaseUrl === backendUrl.href
      ? activeWithdrawal.operationId
      : undefined;
  useEffect(() => {
    const generation = ++creationGeneration.current;
    if (withdrawalOperationId === undefined) {
      setFailure(undefined);
      void doSilentStart(generation);
    }
    return () => {
      creationGeneration.current = generation + 1;
    };
  }, [doSilentStart, withdrawalOperationId]);

  if (failure) {
    return {
      status: "failed",
      error: failure,
    };
  }

  if (!withdrawalOperationId) {
    return {
      status: "loading",
      error: undefined,
    };
  }

  return function WithdrawalState(): utils.RecursiveState<State> {
    return useWithdrawalOperationState({
      operationId: withdrawalOperationId,
      routeClose,
      onAbort,
      onContinueLater,
      onOperationCreated,
      focus,
      clearWhenTerminal: true,
    });
  };
}

export function useWithdrawalOperationState({
  operationId,
  routeClose,
  onAbort,
  onContinueLater,
  onOperationCreated: _onOperationCreated,
  focus,
  clearWhenTerminal = false,
}: WithdrawalOperationProps): State {
  const polledOperationId = useRef(operationId);
  const polledResult = useWithdrawalDetails(operationId);
  const result =
    polledOperationId.current === operationId ? polledResult : undefined;
  polledOperationId.current = operationId;
  const { i18n } = useTranslationContext();
  const [bankState, updateBankState] = useBankState();
  const {
    lib: { bank },
  } = useBankCoreApiContext();

  const terminal = result !== undefined && isTerminalWithdrawalResult(result);

  useEffect(() => {
    if (
      clearWhenTerminal &&
      terminal &&
      bankState.activeWithdrawal?.operationId === operationId
    ) {
      updateBankState("activeWithdrawal", undefined);
    }
  }, [
    bankState.activeWithdrawal?.operationId,
    clearWhenTerminal,
    operationId,
    terminal,
    updateBankState,
  ]);

  if (!result) {
    return { status: "loading", error: undefined };
  }
  return buildWithdrawalOperationState({
    result,
    operationId,
    bankIntegrationApiBaseUrl: bank.getIntegrationAPI().href as HostPortPath,
    loadingErrorTitle: i18n.str`Failed to load withdrawal details.`,
    onRetry: () => void revalidateWithdrawalDetails(),
    routeClose,
    onAbort,
    onContinueLater,
    focus,
  });
}
